import { getRemovebgCreditsQuery, getRemovebgOrdersQuery, getActiveOrganizationsQuery } from '../queries/ai_credits';
import config from '../../api_config.json';

// Controller functions for AI Credits fetching and data aggregation

// Function to fetch credits purchases (where paidCreditsAmount > 0 AND note = "Acquisto Crediti IA")
export const fetchAcquistoCrediti = async (token, isProd, pageSize = 100, skip = 0, startDate = null, endDate = null) => {
  const env = isProd ? "prod" : "dev";
  const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

  // Build query for purchases
  const query = `
    query GetRemovebgOrders($first: Int!, $skip: Int, $order: [Removebg_ordersOrder!], $where: Removebg_ordersWhereInput) {
      findRemovebgOrders(
        first: $first,
        skip: $skip,
        order: $order,
        where: $where
      ) {
        count
        edges {
          node {
            objectId
            freeCreditsAmount
            paidCreditsAmount
            note
            paymentReference
            isPaid
            createdAt
            org_pointer {
              objectId
              nome
            }
          }
        }
      }
    }
  `;

  // Define where conditions
  let whereCondition = {
    paidCreditsAmount: { greaterThan: 0 },
    note: { equalTo: "Acquisto Crediti IA" }
  };

  // Add date range if provided
  if (startDate && endDate) {
    whereCondition.createdAt = {
      greaterThanOrEqualTo: startDate.toISOString(),
      lessThanOrEqualTo: endDate.toISOString()
    };
  }

  try {
    const response = await fetch(graphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        query,
        variables: {
          first: pageSize,
          skip: skip,
          order: ["createdAt_DESC"],
          where: whereCondition
        },
      }),
    });

    const result = await response.json();
    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    const orders = result.findRemovebgOrders?.edges.map(edge => {
      // Try to safely extract org info, with fallbacks if not available
      const orgId = edge.node.org_pointer?.objectId || null;
      let orgName = edge.node.org_pointer?.nome || null;

      // Fallback if organization info isn't available
      if (!orgName) {
        orgName = "Transaction #" + edge.node.objectId.substring(0, 6);
      }

      return {
        id: edge.node.objectId,
        orgId: orgId,
        orgName: orgName,
        freeCredits: edge.node.freeCreditsAmount || 0,
        paidCredits: edge.node.paidCreditsAmount || 0,
        isPaid: edge.node.isPaid || false,
        note: edge.node.note,
        createdAt: edge.node.createdAt,
        paymentReference: edge.node.paymentReference
      };
    }) || [];
    
    return {
      orders,
      count: result.findRemovebgOrders?.count || 0
    };
  } catch (error) {
    console.error("Error fetching credits purchases:", error);
    throw error;
  }
};

export const fetchRemovebgCredits = async (token, isProd, pageSize = 20, skip = 0) => {
  const env = isProd ? "prod" : "dev";
  const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

  try {
    const response = await fetch(graphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: getRemovebgCreditsQuery,
        variables: {
          first: pageSize,
          skip: skip,
          order: ["paidCreditsAmount_DESC", "freeCreditsAmount_DESC"]
        },
      }),
    });

    const result = await response.json();
    
    if (result.errors) {
      throw new Error(result.errors[0].message);
    }
    
    const credits = result.findRemovebgCredits?.edges.map(edge => {
      // Try to safely extract org info, with fallbacks if not available
      const orgId = edge.node.org_pointer?.objectId || null;
      let orgName = edge.node.org_pointer?.nome || null;
      
      // Fallback if organization info isn't available
      if (!orgName) {
        orgName = "Credit #" + edge.node.objectId.substring(0, 6);
      }
      
      return {
        id: edge.node.objectId,
        orgId: orgId,
        orgName: orgName,
        freeCredits: edge.node.freeCreditsAmount || 0,
        paidCredits: edge.node.paidCreditsAmount || 0,
        totalCredits: (edge.node.freeCreditsAmount || 0) + (edge.node.paidCreditsAmount || 0),
        createdAt: edge.node.createdAt
      };
    }) || [];
    
    return {
      credits,
      count: result.findRemovebgCredits?.count || 0
    };
  } catch (error) {
    console.error("Error fetching RemoveBG credits:", error);
    throw error;
  }
};

export const fetchRemovebgOrders = async (token, isProd, pageSize = 100, skip = 0, startDate = null, endDate = null) => {
  const env = isProd ? "prod" : "dev";
  const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

  // Build where condition if dates are provided
  let whereCondition = {};
  if (startDate && endDate) {
    whereCondition.createdAt = {
      greaterThanOrEqualTo: startDate,
      lessThanOrEqualTo: endDate
    };
  }

  try {
    const response = await fetch(graphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: getRemovebgOrdersQuery,
        variables: {
          first: pageSize,
          skip: skip,
          order: ["createdAt_DESC"],
          where: Object.keys(whereCondition).length > 0 ? whereCondition : undefined
        },
      }),
    });

    const result = await response.json();
    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    const orders = result.findRemovebgOrders?.edges.map(edge => {
      // Try to parse the note field to extract feature information
      let feature = 'Rimuovi sfondo'; // Default to remove background
      let noteData = {};
      let note = edge.node.note;

      // Only attempt to parse if it's likely to be JSON
      if (note && typeof note === 'string' && note.trim().startsWith('{') && note.trim().endsWith('}')) {
        try {
          noteData = JSON.parse(note);
          if (noteData && noteData.feature) {
            feature = noteData.feature;
          }
        } catch (e) {
          // If parsing fails, keep default
          console.log('Failed to parse note JSON:', e);
        }
      }

      // Try to safely extract org info, with fallbacks if not available
      const orgId = edge.node.org_pointer?.objectId || null;
      let orgName = edge.node.org_pointer?.nome || null;

      // Fallback if organization info isn't available
      if (!orgName) {
        orgName = "Transaction #" + edge.node.objectId.substring(0, 6);
      }

      return {
        id: edge.node.objectId,
        orgId: orgId,
        orgName: orgName,
        freeCredits: edge.node.freeCreditsAmount || 0,
        paidCredits: edge.node.paidCreditsAmount || 0,
        isPaid: edge.node.isPaid || false,
        feature: feature,
        originalNote: note, // Store original note for debugging
        createdAt: edge.node.createdAt,
        paymentReference: edge.node.paymentReference
      };
    }) || [];
    
    return {
      orders,
      count: result.findRemovebgOrders?.count || 0
    };
  } catch (error) {
    console.error("Error fetching RemoveBG orders:", error);
    throw error;
  }
};

// New function to fetch ALL removebg orders with pagination and date filtering
export const fetchAllRemovebgOrders = async (token, isProd, startDate = null, endDate = null, onProgress = null) => {
  const BATCH_SIZE = 10000;
  let allOrders = [];
  let currentSkip = 0;
  let hasMore = true;
  let totalCount = 0;

  while (hasMore) {
    try {
      const { orders, count } = await fetchRemovebgOrders(token, isProd, BATCH_SIZE, currentSkip, startDate, endDate);
      allOrders = allOrders.concat(orders);
      totalCount = count; // Store the total count
      
      // Check if we've fetched all records
      currentSkip += BATCH_SIZE;
      hasMore = currentSkip < count;
      
      // Calculate progress percentage
      const progress = Math.min(100, Math.round((allOrders.length / count) * 100));
      
      
      // Call progress callback if provided
      if (onProgress) {
        onProgress({
          fetched: allOrders.length,
          total: count,
          percentage: progress
        });
      }
    } catch (error) {
      console.error(`Error fetching batch at skip ${currentSkip}:`, error);
      throw error;
    }
  }

  return { orders: allOrders, count: totalCount };
};

// Helper function to group and aggregate data
export const aggregateFeatureUsage = (orders) => {
  const featureMap = orders.reduce((acc, order) => {
    const feature = order.feature;
    if (!acc[feature]) {
      acc[feature] = {
        feature,
        count: 0,
        freeCredits: 0,
        paidCredits: 0
      };
    }

    acc[feature].count++;

    // Only account for usage (negative values)
    const freeUsage = (order.freeCredits && order.freeCredits < 0) ? Math.abs(order.freeCredits) : 0;
    const paidUsage = (order.paidCredits && order.paidCredits < 0) ? Math.abs(order.paidCredits) : 0;

    acc[feature].freeCredits += freeUsage;
    acc[feature].paidCredits += paidUsage;
    return acc;
  }, {});

  // Convert to array and calculate percentages
  const featuresArray = Object.values(featureMap);
  const totalCredits = featuresArray.reduce((sum, item) => sum + item.freeCredits + item.paidCredits, 0);

  return featuresArray.map(item => ({
    ...item,
    totalCredits: item.freeCredits + item.paidCredits,
    percentage: totalCredits > 0 ? ((item.freeCredits + item.paidCredits) / totalCredits) * 100 : 0
  }));
};

// Function to fetch all active organizations
export const fetchActiveOrganizations = async (token, isProd) => {
  const env = isProd ? "prod" : "dev";
  const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
  
  const BATCH_SIZE = 1000;
  let allOrgs = [];
  let currentSkip = 0;
  let hasMore = true;
  
  // Where condition for active organizations
  const whereCondition = {
    tipo_istanza: { equalTo: "production" },
    stato: { equalTo: "attiva" }
  };
  
  while (hasMore) {
    try {
      const response = await fetch(graphqlUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: getActiveOrganizationsQuery,
          variables: {
            first: BATCH_SIZE,
            skip: currentSkip,
            where: whereCondition
          },
        }),
      });

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      const orgs = result.organizzaziones?.edges.map(edge => ({
        objectId: edge.node.objectId,
        nome: edge.node.nome,
        stato: edge.node.stato,
        tipo_istanza: edge.node.tipo_istanza
      })) || [];
      
      allOrgs = allOrgs.concat(orgs);
      const totalCount = result.organizzaziones?.count || 0;
      
      // Check if we've fetched all records
      currentSkip += BATCH_SIZE;
      hasMore = currentSkip < totalCount;
    } catch (error) {
      console.error(`Error fetching active organizations at skip ${currentSkip}:`, error);
      throw error;
    }
  }
  
  return allOrgs;
};